'use client';

import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { dispatchButtonTracking } from '@packages/shared/src/ui/Button/dispatchButtonTracking';
import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
  CardDescription,
} from '@packages/shared/src/ui/Card/Card';
import { Notification } from '@packages/shared/src/ui/Notification/Notification';
import { InvoiceDetail } from '../Invoice/InvoiceDetail.client';

import type { InvoiceCardProps } from './types';
import { messages } from './messages';
import { ProductImageRowList } from '../ProductImageRowList/ProductImageRowList.client';

export const InvoiceCard = ({ invoice }: InvoiceCardProps) => {
  const { formatMessage } = useIntl();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const dispatchGlycerin = useTracking();
  const {
    dueDateFormatted,
    invoiceDate,
    invoiceId,
    invoiceNumber,
    paylink,
    paymentHint,
    products,
    status,
  } = invoice;

  // GALAXY-241: Show paylink button only if paylink exists and status is 'Open'
  const showPaylink = Boolean(paylink && status === 'Open');

  const handlePaylinkClick = () => {
    if (paylink) {
      window.open(paylink, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Card key={invoiceId} hasAutoSeparation>
      <CardHeader hasDivider className="text-size-body-sm justify-between">
        <div>
          <CardTitle className="text-size-body-sm font-bold">
            {formatMessage(messages.invoiceTitle)}
          </CardTitle>
          <CardDescription className="text-color-text-darktransparent">
            {formatMessage(messages.invoiceFromDate, { date: invoiceDate })}
          </CardDescription>
        </div>
        {status && (
          <span
            className={`rounded-full font-bold ${
              status === 'Paid' ? 'text-color-success-dark' : 'text-color-error'
            }`}
          >
            {status === 'Paid'
              ? formatMessage(messages.invoiceStatusPaid)
              : formatMessage(messages.invoiceStatusOpen)}
          </span>
        )}
      </CardHeader>
      <CardContent>
        {invoiceDate && (
          <p className="text-size-body-xs">
            {formatMessage(messages.dueDateLabel, { date: dueDateFormatted })}
          </p>
        )}
        {paymentHint && (
          <Notification severity="info" variant="outline" className="mt-1">
            <span className="text-size-body-xs">{paymentHint}</span>
          </Notification>
        )}
        {products && products.length > 0 && (
          <div className="mt-1 mb-1.5 flex justify-start gap-2">
            <ProductImageRowList
              items={products}
              imageSize="large"
              additionalItemsCount={products.length > 3 ? products.length - 3 : 0}
              keyPrefix={invoiceId}
            />
          </div>
        )}
      </CardContent>
      <CardContent className="flex cursor-pointer flex-row p-1.5">
        <button
          type="button"
          className="text-size-body-sm flex items-center"
          onClick={() => {
            dispatchButtonTracking({
              eventDispatcher: dispatchGlycerin,
              type: 'button',
              clickTrackingProps: {
                category: 'Rechnung anzeigen',
                label: formatMessage(messages.viewInvoiceLabel, { invoiceNumber }),
              },
            });
            setIsDialogOpen(true);
          }}
        >
          <span className="icon-emp-billing mr-1 size-3" />
          {formatMessage(messages.viewInvoiceLabel, {
            invoiceNumber,
          })}
        </button>
      </CardContent>
      {showPaylink && (
        <CardContent className="flex cursor-pointer flex-row p-1.5">
          <button
            type="button"
            className="text-size-body-sm flex items-center"
            onClick={handlePaylinkClick}
            aria-label={formatMessage(messages.paylinkAriaLabel, { invoiceNumber })}
          >
            <span className="icon-emp-creditcard mr-1 size-3" />
            {formatMessage(messages.paylinkButton)}
          </button>
        </CardContent>
      )}
      {isDialogOpen && (
        <InvoiceDetail onClose={() => setIsDialogOpen(false)} invoiceNumber={invoiceNumber} />
      )}
    </Card>
  );
};
